import { removeHost } from '@qctsw/common'
import { e } from 'unocss'
import { useEmit } from '~/composables/useEventBus'
import { addUserCarApi, publishComplaintApi } from '~/server'
import { addLogApi } from '~/server/modules/log'
import type { IssueParam, PublishComplaintParams, UserCarParams } from '~/server/types'
import { type CAR_INSURANCE_CODE, ISSUE_TYPE_CODE_ENUM } from '~/shared/constants'

export function usePublishSubmit() {
  async function publishSubmit(params: PublishComplaintParams) {
    try {
      const _result = await publishComplaintApi(params)
      useMessage.success('投诉发布成功')
      return _result
    }
    catch (err) {
      addLogApi({
        data: JSON.stringify(params),
        code: err?.code || '',
        url: window.location.href || '',
        errorMsg: JSON.stringify(err),
        remark: '投诉发布失败',
      })
      console.error(typeof err)
      useDialog.error({
        title: '投诉发布失败',
        content: getErrorMsg(err),
        positiveText: '确定',
        negativeText: '反馈问题',
        onNegativeClick: () => {
          // TODO: 反馈问题弹框
          useEmit('feedback')
        },
      })
      return false
    }
  }

  async function createDriving(publishParams: PublishComplaintParams) {
    const { drivingInfo } = usePublishStore()
    const params = formatDrivingParam(publishParams, drivingInfo)
    try {
      await addUserCarApi(params)
      useMessage.success('车辆信息添加成功')
    }
    catch (err) {
      console.error(err)
    }
  }
  function formatDrivingParam(
    publishParams: PublishComplaintParams,
    drivingInfo?: Record<string, any>,
  ): UserCarParams {
    const params = {
      ...drivingInfo,

      carUserName: publishParams.carUserName,
      carUserSex: publishParams.userSex,
      carUserPhone: publishParams.userPhone,
      carUserEmail: publishParams.userEmail,

      carBrandId: publishParams.brandId,
      carBrandVendorId: publishParams.brandVendorId,
      carVendorId: publishParams.vendorId,
      carSeriesId: publishParams.seriesId,
      carModelId: publishParams.modelId,
      carRegionPlate: publishParams.regionPlate,
      carNumberPlate: publishParams.numberPlate,
      carEngineNumber: publishParams.engineNumber, // 发动机编号
      carInsuranceCode: publishParams.insuranceCode, // 车保code
      carShoppingTime: publishParams.shoppingTime,
      firstFaultTime: publishParams.firstFaultTime,
      carTravelKm: publishParams.travelKm,
      carVin: publishParams.vin,

      // 经销商信息
      carDealerProvinceId: publishParams.carDealerProvinceId,
      carDealerCityId: publishParams.carDealerCityId,
      carDealerId: publishParams.carDealerId,
      carDealerName: publishParams.carDealerName,
      carDealerLinkman: publishParams.carDealerLinkman,
      carDealerPhone: publishParams.carDealerPhone,
    }

    return params as unknown as UserCarParams
  }

  return {
    publishSubmit,
    createDriving,
  }
}

export function formatPublishParams() {
  const { formData } = usePublishStore()

  if (!formData.isAccept) {
    useMessage.warning('请阅读并同意用户协议')
    return
  }

  const params: PublishComplaintParams = {
    // base
    collectiveId: formData.collectiveId ?? undefined,
    refComplainId: formData.refComplainId ?? undefined,
    refComplainContent: formData.refComplainContent ?? undefined,
    // user
    carUserName: formData.userName,
    userEmail: formData.email,
    userPhone: formData.phone,
    userSex: formData.sex,
    travelKm: formData.carTravelKm,
    // 车辆品牌
    brandId: formData.carBrandId,
    brandVendorId: formData.carBrandVendorId,
    vendorId: formData.carVendorId as number,
    seriesId: formData.carSeriesId,
    modelId: formData.carModelId,
    // 车辆信息
    insuranceCode: formData.carInsuranceCode as typeof CAR_INSURANCE_CODE[number]['value'],
    vin: formData.carVin,
    engineNumber: formData.carEngineNumber,
    regionPlate: formData.carRegionPlate,
    numberPlate: formData.carRegionPlate === '未上牌' ? '' : formData.carNumberPlate,

    // 经销商信息
    carDealerId: formData.carDealerId === -1 ? undefined : Number(formData.carDealerId),
    carDealerPhone: formData.carDealerPhone,
    carDealerLinkman: formData.carDealerLinkman,
    carDealerName: formData.carDealerName,
    carDealerProvinceId: Number(formData.carDealerAreaIds[0]),
    carDealerCityId: Number(formData.carDealerAreaIds[1]),
    // 问题信息
    shoppingTime: formData.carShoppingTime as string,
    firstFaultTime: formData.firstFaultTime as string,
    title: formData.title,
    content: formData.content,
    issueLs: [],
    audioLs: formData.audios.map(url => removeHost(url)).filter(Boolean).filter(item => !item.trim().startsWith('blob:')),
    imgLs: formData.pictures.map(url => removeHost(url)).filter(Boolean).filter(item => !item.trim().startsWith('blob:')),
    videoLs: formData.videos.map(url => removeHost(url)).filter(Boolean).filter(item => !item.trim().startsWith('blob:')),
    clientTypeCode: 0, // pc
    clientPortCode: 0, // 投诉来源端口：0.投诉网站 1.投诉手机端 2.网评网站 3.网评手机端
    isSite: formData.isSite,
    isWillingToLive: formData.isWillingToLive || false,
  }

  if (formData.quality.length) {
    for (const item of formData.quality) {
      const [fid, issueId, partId] = item.split('-')
      const data: IssueParam = {
        issueTypeCode: ISSUE_TYPE_CODE_ENUM.quality,
        issueId: Number(fid),
        childIssueId: Number(issueId),
      }
      if (partId)
        data.partsId = Number(partId)
      params.issueLs.push(data)
    }
  }

  if (formData.sale.length) {
    for (const item of formData.sale) {
      const [fid, issueId] = item.split('-')
      const data: IssueParam = {
        issueTypeCode: ISSUE_TYPE_CODE_ENUM.sale,
        issueId: Number(fid),
        childIssueId: Number(issueId),
      }
      params.issueLs.push(data)
    }
  }
  if (formData.afterSale.length) {
    for (const item of formData.afterSale) {
      const [fid, issueId] = item.split('-')
      const data: IssueParam = {
        issueTypeCode: ISSUE_TYPE_CODE_ENUM.afterSale,
        issueId: Number(fid),
        childIssueId: Number(issueId),
      }
      params.issueLs.push(data)
    }
  }
  if (formData.appeal.length) {
    for (const item of formData.appeal) {
      const data: IssueParam = {
        issueTypeCode: ISSUE_TYPE_CODE_ENUM.appeal,
        issueId: Number(item),
      }
      params.issueLs.push(data)
    }
  }

  // title拼接品牌车系
  const carInfo = [
    formData.carBrandName,
    formData.carSeriesName,
  ].filter(v => !!v).join('')
  // console.log('🚀 ~ formatPublishParams ~ carInfo:', carInfo)
  // 再次投诉时，不需要拼接品牌车系
  if (!params.title.includes(carInfo))
    params.title = `${carInfo}-${params.title}`

  // eslint-disable-next-line no-console
  console.log('params', params)
  // console.log('params', formData)
  return params
}
